import { useHistory } from '@useHistory/useHistoryCompat';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NetworkRequestOptions } from '../../core/interfaces/request';
import { AuthService } from '../services/auth.service';
import useService from '../../injector/hooks/useService';

export function useAuthGuard() {
  const history = useHistory();
  const authService = useService(AuthService);

  const loginWithSessionToken = async (sessionToken: string) => {
    return authService
      .loginWithSessionToken(sessionToken)
      .pipe(
        map(() => {
          history.replace(history.location.pathname);
          return true;
        }),
        catchError(error => {
          if (error.status === 401) {
            return authService.onUnAuthorisedEvent(false).pipe(map(() => false));
          }
          return of(false);
        })
      )
      .toPromise();
  };

  // Exchanges the one-time session_token a region switch arrives with and drops it from the URL.
  // Passes straight through when there is none, so it is safe to run on every load.
  const executeSessionTokenGuard = async () => {
    const sessionToken = new URLSearchParams(history.location?.search).get('session_token');

    if (!sessionToken) {
      return true;
    }

    return loginWithSessionToken(sessionToken);
  };

  const executeAuthGuard = async () => {
    const requestOptions: NetworkRequestOptions = {
      uiOptions: {
        handleUnauthorisedResponse: false,
        showLoading: false
      }
    };

    if (!(await executeSessionTokenGuard())) {
      return false;
    }

    return authService
      .getLoggedInUser(requestOptions, true)
      .pipe(
        map(() => true),
        catchError((error: any) => {
          if (error.status === 401) {
            return authService.onUnAuthorisedEvent(true).pipe(map(() => false));
          }

          return of(false);
        })
      )
      .toPromise();
  };

  return {
    executeSessionTokenGuard,
    executeAuthGuard
  };
}
